import type { FuelEstimate } from './types'

/**
 * Generator / reefer fuel — the diesel a tour bus genset or a trailer reefer
 * burns on top of what the engine uses to move.
 *
 * Model (deliberately simple, like the rest of the estimator):
 *   hours  = drive time on this route (the genset/reefer runs while rolling)
 *            + any parked hours the user adds (overnights, load-in days)
 *   gallons = hours × gallons-per-hour
 *   cost    = gallons × the route's average price per gallon (the genset
 *             draws from the same tank, so it pays the same blended price)
 *
 * Derived from a finished estimate on the client, so toggling it on/off or
 * changing the rate recalculates instantly without re-running the route.
 */

/** Accepted range for a custom gallons-per-hour override. */
export const GPH_MIN = 0.1
export const GPH_MAX = 5

/** Accepted range for parked (non-driving) generator hours on one route. */
export const IDLE_HOURS_MAX = 240

export interface GeneratorLine {
	/** Total running hours: drive + parked */
	hours: number
	driveHours: number
	idleHours: number
	gallonsPerHour: number
	gallons: number
	cost: number
}

export function generatorLine(
	estimate: FuelEstimate,
	gallonsPerHour: number,
	idleHours = 0,
): GeneratorLine {
	const driveHours = estimate.totalDurationMinutes / 60
	const hours = driveHours + idleHours
	const gallons = hours * gallonsPerHour
	const pricePerGallon = estimate.totalGallons > 0 ? estimate.totalCost / estimate.totalGallons : 0
	return {
		hours,
		driveHours,
		idleHours,
		gallonsPerHour,
		gallons: round(gallons, 1),
		cost: round(gallons * pricePerGallon, 2),
	}
}

function round(value: number, digits: number): number {
	const f = 10 ** digits
	return Math.round(value * f) / f
}
